import React, { useEffect, useRef } from 'react'
import { BrowserRouter as Router, useLocation, Navigate } from 'react-router-dom'
import useThemeStore from './stores/themeStore'
import Layout from './components/Layout'
import ErrorBoundary from './components/ui/ErrorBoundary'
import ResearchPage from './pages/ResearchPage'
import SourcesPage from './pages/SourcesPage'
import ActivityPage from './pages/ActivityPage'
import SettingsPage from './pages/SettingsPage'
import OverviewsPage from './pages/OverviewsPage'
import LoginPage from './pages/LoginPage'
import { useAuth } from './hooks/useAuth'

// Transcription & Translation are no longer top-level destinations — transcription
// folds into Sources (a Transcripts library) and translation surfaces as actions
// in Research. See doc decision + the Sources/Research workspaces.
const TABS = [
  ['/research', ResearchPage],
  ['/sources', SourcesPage],
  ['/overviews', OverviewsPage],
  ['/activity', ActivityPage],
  ['/settings', SettingsPage],
]

// Keep every VISITED tab mounted and just toggle visibility, so switching tabs
// preserves each page's state and in-flight work (search results, ask answers, a
// running import + its progress polling, the selected project, scroll position…).
//
// Visited, not all: mounting all four up front made a page load fire every tab's
// queries at once, so opening Research also fetched Settings, Sources and
// Activity. Wasteful when the engines are healthy, and painful when they are not
// — a reload became a dozen simultaneous requests, several of them engine-backed
// and each able to sit out a 30-180s timeout. A tab now mounts on first visit and
// stays mounted, which keeps the state-preserving behaviour and drops the initial
// burst to the one page actually being looked at.
function Tabs() {
  const { pathname } = useLocation()
  const known = TABS.some(([p]) => p === pathname)
  // A ref, not state: recording a visit must take effect in THIS render (the tab
  // has to appear immediately) and it never needs to trigger one of its own.
  // Adding to a Set is idempotent, so StrictMode's double render is harmless.
  const visited = useRef(new Set())
  if (known) visited.current.add(pathname)

  // Unknown paths (incl. the old /search) fall back to Research.
  if (!known) return <Navigate to="/research" replace />
  return (
    <>
      {TABS.filter(([path]) => visited.current.has(path)).map(([path, Page]) => (
        <div key={path} style={{ display: pathname === path ? 'block' : 'none' }}>
          <ErrorBoundary fallbackMessage="This view failed to render." showError>
            <Page />
          </ErrorBoundary>
        </div>
      ))}
    </>
  )
}

// The gate. Nothing inside Layout mounts until the server says who we are —
// mounting the tabs first would fire a burst of API calls that all 401, and
// would flash the app shell at someone who is not signed in.
function Gate() {
  const { loading, enabled, user, setupRequired, mustChangePassword } = useAuth()

  if (loading) {
    return <div className="min-h-screen bg-parchment-100 dark:bg-midnight-900" aria-busy="true" />
  }
  // VERALIS_AUTH=off — the layer is transparent, the app behaves as it always did.
  if (enabled) {
    if (setupRequired) return <LoginPage mode="setup" />
    if (!user) return <LoginPage mode="login" />
    if (mustChangePassword) return <LoginPage mode="change" />
  }
  return (
    <Layout>
      <Tabs />
    </Layout>
  )
}

export default function App() {
  const initializeTheme = useThemeStore((s) => s.initializeTheme)

  useEffect(() => {
    initializeTheme()
  }, [initializeTheme])

  return (
    <Router>
      <Gate />
    </Router>
  )
}
